import { useState, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Share, WhatsappLogo, Copy, Download, TwitterLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { shareLink } from "@/lib/publicUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dialog showing a match's auto-generated share card PNG.
 * Lets the user download, copy the link, share to WhatsApp or X.
 */
export default function ShareMatchDialog({ open, onOpenChange, match }) {
  const [bust, setBust] = useState(() => Date.now());

  const imgSrc = useMemo(() => {
    if (!match) return "";
    // Cache-bust on open so edits to the match reflect in the preview
    return `${API}/matches/${match.id}/share-card.png?v=${bust}`;
  }, [match, bust]);

  const matchUrl = useMemo(() => {
    if (!match) return "";
    return shareLink(`matches/${match.id}`);
  }, [match]);

  const shareText = useMemo(() => {
    if (!match) return "";
    const p1 = match.player1_name || "Player 1";
    const p2 = match.player2_name || "Player 2";
    const s = `${match.player1_score ?? 0}-${match.player2_score ?? 0}`;
    const winner = match.winner_id === match.player1_id ? p1 :
      match.winner_id === match.player2_id ? p2 : null;
    const tail = winner ? ` — ${winner} wins!` : "";
    const t = match.tournament_name ? ` · ${match.tournament_name}` : "";
    return `🎱 ${p1} ${s} ${p2}${tail}${t}\n${matchUrl}`;
  }, [match, matchUrl]);

  if (!match) return null;

  const downloadPng = async () => {
    try {
      const res = await fetch(imgSrc, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `match-${match.id}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed — try right-click → Save Image");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Caption copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const shareWhatsApp = () => {
    // shareText now includes the canonical match URL (uses REACT_APP_PUBLIC_URL
    // when set, falling back to the current origin). Configure your custom
    // domain in /app/frontend/.env to keep all share links on-brand.
    const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const shareX = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const tryNativeShare = async () => {
    if (!navigator.share) return shareWhatsApp();
    try {
      const res = await fetch(imgSrc, { credentials: "include" });
      const blob = await res.blob();
      const file = new File([blob], `match-${match.id}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: "Match Result",
          text: shareText,
          files: [file],
        });
      } else {
        await navigator.share({ title: "Match Result", text: shareText });
      }
    } catch {
      // user cancelled or share API rejected — no toast needed
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (v) setBust(Date.now()); }}>
      <DialogContent className="max-w-2xl" data-testid="share-match-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share className="w-5 h-5 text-primary" weight="fill" />
            Share Match Result
          </DialogTitle>
          <DialogDescription>
            Auto-generated card ready for WhatsApp, Instagram or X.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg overflow-hidden border border-border bg-muted/20" data-testid="share-card-preview">
          <img
            src={imgSrc}
            alt="Match share card"
            className="w-full block"
            style={{ aspectRatio: "1200 / 630" }}
            data-testid="share-card-img"
          />
        </div>

        <p className="text-[11px] text-muted-foreground text-center mt-2">
          Caption: <span className="text-foreground">{shareText}</span>
        </p>

        <DialogFooter className="flex-wrap gap-2">
          <Button
            variant="outline" size="sm"
            onClick={copyLink}
            data-testid="copy-share-link"
          >
            <Copy className="w-4 h-4 mr-1" />Copy Caption
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={downloadPng}
            data-testid="download-share-png"
          >
            <Download className="w-4 h-4 mr-1" />Download PNG
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={shareX}
            data-testid="share-x-btn"
          >
            <TwitterLogo className="w-4 h-4 mr-1" />Share to X
          </Button>
          <Button
            size="sm"
            className="bg-[#25D366] hover:bg-[#1DAE53] text-white"
            onClick={tryNativeShare}
            data-testid="share-whatsapp-btn"
          >
            <WhatsappLogo className="w-4 h-4 mr-1" weight="fill" />Share to WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
